import Vue from 'vue'
import Vuex, { Store } from 'vuex'
import { Menu } from "@/modules/Menu";
import { MainteInfo } from "@/modules/MainteInfo";
import createPersistedState from 'vuex-persistedstate';
import * as Common from '../commons/common';
import router from "@/router/index";
import isMobileJS from 'ismobilejs'
import { SheetListCondition } from '@/modules/teacher/SheetList';
import { SumupListCondition } from '@/modules/teacher/SumupList';
import { ResultListCondition } from '@/modules/teacher/ResultList';

Vue.use(Vuex)

export type StateType = {
  currentUser: any
  isShowOverlay: boolean
  overlayText: string
  curriculums: any[]
  nendos: any[]
  viewPoints: any[]
  grades: any[]
  months: any[]
  kinds: any[]
  roles: any[]
  correct_texts: any[]
  scoring_types: any[]
  sheetListCondition: SheetListCondition
  sumupListCondition: SumupListCondition
  resultListCondition: ResultListCondition
  isMobile: boolean
  isHeaderDisp: boolean
  mainteInfo: MainteInfo
  resultDistributionRoomId: string
  resultStudentDetailSheetGroupKey: string
  resultStudentDetailRoomId: string
  student_num: string
  grade_id: number
}

export default new Vuex.Store({
  state: {
    currentUser: null,
    isShowOverlay: false,
    overlayText: "",
    curriculums: [],
    nendos: [],
    viewPoints: [],
    grades: Common.GetComboboxGradeArray(),
    months: Common.GetComboboxMonthArray(),
    kinds: Common.GetComboboxKindArray(),
    roles: Common.GetComboboxRoleArray(),
    correct_texts: Common.GetComboboxCorrectTextArray(),
    scoring_types: Common.GetComboboxScoingTypeArray(),
    sheetListCondition: null,
    sumupListCondition: null,
    resultListCondition: null,
    isMobile: isMobileJS(window.navigator).phone || isMobileJS(window.navigator).tablet,
    isHeaderDisp: true,
    mainteInfo: new MainteInfo([]),
    resultDistributionRoomId: "",
    resultStudentDetailSheetGroupKey: "",
    resultStudentDetailRoomId: "",
  } as StateType,
  mutations: {
    setCurrentUser(state, user: any){
      state.currentUser = user;
    },
    setCurriculums(state, curriculums: []){
      state.curriculums = curriculums;
    },
    setNendos(state, nendos: []){
      state.nendos = nendos;
    },
    setViewPoints(state, viewPoints: []){
      state.viewPoints = viewPoints;
    },
    setSheetListCondition(state, condition: SheetListCondition){
      state.sheetListCondition = condition;
    },
    setSumupListCondition(state, condition: SumupListCondition){
      state.sumupListCondition = condition;
    },
    setResultListCondition(state, condition: ResultListCondition){
      state.resultListCondition = condition;
    },
    setIsShowOverlay(state, isShow: boolean){
      state.isShowOverlay = isShow;
    },
    setOverlayText(state, text: string){
      state.overlayText = text;
    },
    setIsHeaderDisp(state, isHeaderDisp: boolean){
      state.isHeaderDisp = isHeaderDisp;
    },
    setMainteInfo(state, mainteInfos: any[]){
      state.mainteInfo = new MainteInfo(mainteInfos);
    },
    setResultDistributionRoomId(state, roomId: string){
      state.resultDistributionRoomId = roomId;
    },
    setResultStudentDetailSheetGroupKey(state, sheetGroupKey: string){
      state.resultStudentDetailSheetGroupKey = sheetGroupKey;
    },
    setResultStudentDetailRoomId(state, roomId: string){
      state.resultStudentDetailRoomId = roomId;
    },
    setStudentNum(state, student_num: string){
      state.student_num = student_num;
    },
    setGradeId(state, grade_id: number){
      state.grade_id = grade_id;
    }
  },
  getters: {
    // 教科プルダウン
    getComboboxCurriculums: (state) => (allowEmpty: boolean) => {
      const rtn: any[] = [];
      if(allowEmpty){
        rtn.push({value: "", text: "-"});
      }
      for(const curriculum of state.curriculums){
        rtn.push({value: curriculum.id, text: curriculum.name});
      }
      return rtn;
    },
    // 年度プルダウン
    getComboboxNendos: (state) => (allowEmpty: boolean) => {
      const rtn: any[] = [];
      if(allowEmpty){
        rtn.push({value: "", text: "-"});
      }
      for(const nendo of state.nendos){
        rtn.push({value: String(nendo.value), text: String(nendo.value)});
      }
      return rtn;
    },
    // 自分の担当教科プルダウン
    getComboboxMyCurriculums: (state) => (allowEmpty: boolean) => {
      const rtn: any[] = [];
      if(state.currentUser.role == "teacher2"){
        const curriculum_ids = state.currentUser.curriculum_id.split(",");
        if(allowEmpty){
          rtn.push({value: "", text: "-"});
        }
        for(const curriculum of state.curriculums){
          if(curriculum_ids.includes(`${curriculum.id}`)){
            rtn.push({value: curriculum.id, text: curriculum.name});
          }
        }
      }else{
        if(allowEmpty){
          rtn.push({value: "", text: "-"});
        }
        for(const curriculum of state.curriculums){
          rtn.push({value: curriculum.id, text: curriculum.name});
        }
      }
      return rtn;
    },
    // 観点プルダウン
    getComboboxViewPoints: (state) => (allowEmpty: boolean) => {
      const rtn: any[] = [];
      if(allowEmpty){
        rtn.push({value: null, text: "-"});
      }
      for(const viewPoint of state.viewPoints){
        rtn.push({value: viewPoint.id, text: `${viewPoint.id} : ${viewPoint.name}`});
      }
      return rtn;
    },
    // 実施月プルダウン
    getComboboxMonths: (state) => (allowEmpty: boolean) => {
      const rtn: any[] = [];
      if(allowEmpty){
        rtn.push({value: "", text: "-"});
      }
      return rtn.concat(state.months);
    },
    // 学年プルダウン
    getComboboxGrades: (state) => (allowEmpty: boolean) => {
      const rtn: any[] = [];
      if(allowEmpty){
        rtn.push({value: "", text: "-"});
      }
      return rtn.concat(state.grades);
    },
    // 試験種プルダウン
    getComboboxKinds: (state) => (allowEmpty: boolean) => {
      const rtn: any[] = [];
      if(allowEmpty){
        rtn.push({value: "", text: "-"});
      }
      return rtn.concat(state.kinds);
    },
    // 正答プルダウン
    getComboboxCorrectTexts: (state) => (allowEmpty: boolean) => {
      const rtn: any[] = [];
      if(allowEmpty){
        rtn.push({value: null, text: "-"});
      }
      return rtn.concat(state.correct_texts);
    },
    // 採点方法プルダウン
    getComboboxScoringTypes: (state) => (allowEmpty: boolean) => {
      const rtn: any[] = [];
      if(allowEmpty){
        rtn.push({value: null, text: "-"});
      }
      return rtn.concat(state.scoring_types);
    },
    getCurriculumNameById: (state) => (id: number) => {
      const curriculum = state.curriculums.filter(x => x.id == id);
      return curriculum.length > 0 ? curriculum[0].name : "-";
    },
    getViewPointNameById: (state) => (id: number) => {
      const viewPoint = state.viewPoints.filter(x => x.id == id);
      return viewPoint.length > 0 ? viewPoint[0].name : "-";
    },
    getGradeNameByValue: (state) => (value: number) => {
      const grade = state.grades.filter(x => x.value == value);
      return grade.length > 0 ? grade[0].text : "-";
    },
    getKindNameByValue: (state) => (value: number) => {
      const kind = state.kinds.filter(x => x.value == value);
      return kind.length > 0 ? kind[0].text : "-";
    },
    getComboboxRoles: (state) => (allowEmpty: boolean) => {
      const rtn: any[] = [];
      if(allowEmpty){
        rtn.push({value: "", text: "-"});
      }
      return rtn.concat(state.roles);
    },
    getRoleNameByValue: (state) => (value: string) => {
      const role = state.roles.filter(x => x.value == value);
      return role.length > 0 ? role[0].text : "-";
    },
    getCurrentNendo: () => () => {
      var date = new Date();
      date.setMonth(date.getMonth() - 3);
      return date.getFullYear().toString();
    },
    // ヘッダメニュー 権限・オプションにより制御
    headerMenus: (state, getters) => {
      
      const menus: Menu[] = new Array<Menu>();
      
      if(state.currentUser == null){
        return menus;
      }

      if(state.currentUser.role == Common.RoleEnum.Student){
        // 生徒系のメニュー
        // menus.push(new Menu("パスワード変更", "address-card", `/${state.currentUser.school_id}/student_site/password_edit`));
        // menus.push(new Menu("メールアドレス変更", "address-card", `/${state.currentUser.school_id}/student_site/mail_edit`));
        if(state.grade_id >= Common.GradeEnum.Chu1 && state.grade_id <= Common.GradeEnum.Chu3){
          menus.push(new Menu("中継", "video", `/student_site/streaming_view/${state.grade_id}`));
        }
      }else{
        // 教師系のメニュー
        menus.push(new Menu("試験一覧", "file-alt", "/teacher_site/sheet_list"));

        if(state.currentUser.role == Common.RoleEnum.Admin){
          menus.push(new Menu("生徒一覧", "graduation-cap", "/teacher_site/student_list"));
          menus.push(new Menu("教師一覧", "graduation-cap", "/teacher_site/teacher_list"));
          menus.push(new Menu("研究用ID設定", "cog", "/teacher_site/research_id_list"));
          menus.push(new Menu("諸テスト結果出力", "download", "/teacher_site/various_result_download"));
        }

        menus.push(new Menu("動画URL設定", "video", "/teacher_site/streaming_url_edit"));
        menus.push(new Menu("動画視聴ログ", "file-alt", "/teacher_site/streaming_log_list"));

        // if(state.currentUser.role == Common.RoleEnum.Admin){
        //   menus.push(new Menu("メール一括送信", "envelope", "/teacher_site/bulk_send_mail"));
        // }
      }
      return menus;
    },
    // システム管理者か判定
    isAdmin: state => {
      return state.currentUser.role == Common.RoleEnum.Admin;
    },
    isTeacher: state => {
      return state.currentUser.role == Common.RoleEnum.Teacher;
    },
    isStudent: state => {
      return state.currentUser.role == Common.RoleEnum.Student;
    },
    isAdminOrTeacher: state => {
      return state.currentUser.role == Common.RoleEnum.Admin || state.currentUser.role == Common.RoleEnum.Teacher;
    }
  },
  actions: {
    showOverlay(context, overlayText){
      context.commit("setIsShowOverlay", true);
      context.commit("setOverlayText", overlayText == null ? "処理実行中... しばらくお待ちください。" : overlayText);
    },
    hideOverlay(context){
      context.commit("setIsShowOverlay", false);
    },
    goSheetList(context){
      router.push('/teacher_site/sheet_list');
    }
  },
  modules: {
  },
  plugins: [createPersistedState({
    storage: window.sessionStorage
  })],
})
